import { useEffect } from 'react';
import { create } from 'zustand';

export type TraceViewState = {
  scrollPosition?: number;
  detailedScrollPosition?: number;
  expandedSections?: Record<string, boolean>;
  selectedMessageIndex?: number;
  executionSubTab?: string;
};

export type GridScrollAnchor = {
  scrollTop: number;
  firstRowIndex: number;
};

type TabViewStateStore = {
  traceStates: Record<string, TraceViewState>;
  detailSubTabs: Record<string, string>;
  scrollPositions: Record<string, number>;
  gridScrollAnchors: Record<string, GridScrollAnchor>;
  scrollRestorePending: Record<string, true>;

  setTraceViewState: (threadId: string, state: Partial<TraceViewState>) => void;
  getTraceViewState: (threadId: string) => TraceViewState | undefined;
  clearTraceViewState: (threadId: string) => void;

  setDetailSubTab: (entityKey: string, subTab: string) => void;
  getDetailSubTab: (entityKey: string) => string | undefined;

  setScrollPosition: (scrollId: string, scrollTop: number) => void;
  setGridScrollAnchor: (scrollId: string, anchor: GridScrollAnchor) => void;
  markScrollRestorePending: (scrollId: string) => void;
  clearScrollRestorePending: (scrollId: string) => void;
  isScrollRestorePending: (scrollId: string) => boolean;

  clearTabState: (tabId: string) => void;
};

export const signalDetailSubTabKey = (signalId: string) => `signal:${signalId}`;

export const findingDetailSubTabKey = (findingId: string) =>
  `finding:${findingId}`;

export const useTabViewStateStore = create<TabViewStateStore>((set, get) => ({
  traceStates: {},
  detailSubTabs: {},
  scrollPositions: {},
  gridScrollAnchors: {},
  scrollRestorePending: {},

  setTraceViewState: (threadId, state) =>
    set((prev) => ({
      traceStates: {
        ...prev.traceStates,
        [threadId]: {
          ...prev.traceStates[threadId],
          ...state,
        },
      },
    })),

  getTraceViewState: (threadId) => get().traceStates[threadId],

  clearTraceViewState: (threadId) =>
    set((prev) => {
      const { [threadId]: _, ...rest } = prev.traceStates;

      return { traceStates: rest };
    }),

  setDetailSubTab: (entityKey, subTab) =>
    set((prev) => ({
      detailSubTabs: {
        ...prev.detailSubTabs,
        [entityKey]: subTab,
      },
    })),

  getDetailSubTab: (entityKey) => get().detailSubTabs[entityKey],

  setScrollPosition: (scrollId, scrollTop) =>
    set((prev) => ({
      scrollPositions: {
        ...prev.scrollPositions,
        [scrollId]: scrollTop,
      },
    })),

  setGridScrollAnchor: (scrollId, anchor) =>
    set((prev) => ({
      gridScrollAnchors: {
        ...prev.gridScrollAnchors,
        [scrollId]: anchor,
      },
    })),

  markScrollRestorePending: (scrollId) =>
    set((prev) => ({
      scrollRestorePending: {
        ...prev.scrollRestorePending,
        [scrollId]: true,
      },
    })),

  clearScrollRestorePending: (scrollId) =>
    set((prev) => {
      const { [scrollId]: _, ...rest } = prev.scrollRestorePending;

      return { scrollRestorePending: rest };
    }),

  isScrollRestorePending: (scrollId) =>
    Boolean(get().scrollRestorePending[scrollId]),

  clearTabState: (tabId) =>
    set((prev) => {
      const { [tabId]: _trace, ...traceRest } = prev.traceStates;
      const newScrollPositions = Object.fromEntries(
        Object.entries(prev.scrollPositions).filter(
          ([key]) => !key.startsWith(tabId),
        ),
      );
      const newGridScrollAnchors = Object.fromEntries(
        Object.entries(prev.gridScrollAnchors).filter(
          ([key]) => !key.startsWith(tabId),
        ),
      );
      const newScrollRestorePending = Object.fromEntries(
        Object.entries(prev.scrollRestorePending).filter(
          ([key]) => !key.startsWith(tabId),
        ),
      ) as Record<string, true>;

      return {
        traceStates: traceRest,
        scrollPositions: newScrollPositions,
        gridScrollAnchors: newGridScrollAnchors,
        scrollRestorePending: newScrollRestorePending,
      };
    }),
}));

const SCROLL_RESTORE_DELAY_MS = 100;

type ScrollableViewHandle = {
  getScrollElement: () => HTMLDivElement | null;
};

export function useTraceScrollPreservation({
  threadId,
  isLoading,
  isActive,
  viewRef,
  stateKey,
}: {
  threadId: string | undefined;
  isLoading: boolean;
  isActive: boolean;
  viewRef: React.RefObject<ScrollableViewHandle | null>;
  stateKey: keyof Pick<
    TraceViewState,
    'scrollPosition' | 'detailedScrollPosition'
  >;
}) {
  const getTraceViewState = useTabViewStateStore(
    (state) => state.getTraceViewState,
  );
  const setTraceViewState = useTabViewStateStore(
    (state) => state.setTraceViewState,
  );

  useEffect(() => {
    if (!threadId || isLoading || !isActive) return;

    const savedPosition = getTraceViewState(threadId)?.[stateKey];

    if (savedPosition === undefined) return;

    const timeoutId = setTimeout(() => {
      const scrollElement = viewRef.current?.getScrollElement();

      if (scrollElement) {
        scrollElement.scrollTop = savedPosition;
      }
    }, SCROLL_RESTORE_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [threadId, isLoading, isActive, getTraceViewState, stateKey, viewRef]);

  useEffect(() => {
    if (!threadId || !isActive) return;

    const scrollElement = viewRef.current?.getScrollElement();

    if (!scrollElement) return;

    const handleScroll = () => {
      setTraceViewState(threadId, { [stateKey]: scrollElement.scrollTop });
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [threadId, isActive, setTraceViewState, stateKey, viewRef]);
}
